package br.com.vvrefeicoes.portal.domain.model

import org.jetbrains.compose.resources.StringResource
import kotlin.math.roundToLong

data class MonthPeriod(
    val year: Int,
    val month: Int,
) {
    init {
        require(month in 1..12) { "month must be in 1..12" }
    }

    val id: String
        get() = "$year-${month.toString().padStart(2, '0')}"

    fun previous(): MonthPeriod = if (month == 1) MonthPeriod(year = year - 1, month = 12) else MonthPeriod(year = year, month = month - 1)

    fun next(): MonthPeriod = if (month == 12) MonthPeriod(year = year + 1, month = 1) else MonthPeriod(year = year, month = month + 1)
}

/** Unidade com lançamento em uma situação pendente (aguardando aprovação ou rascunho), em uma competência. */
data class PendingEntryUnit(
    val id: String,
    val name: String,
    val year: Int,
    val month: Int,
) {
    val period: MonthPeriod
        get() = MonthPeriod(year = year, month = month)
}

enum class OperationalEntryStatus {
    DRAFT,
    SUBMITTED,
    APPROVED,
    REJECTED,
}

enum class OperationalEntryEventType {
    SAVED,
    SUBMITTED,
    APPROVED,
    REJECTED,
}

data class OperationalEntryEvent(
    val type: OperationalEntryEventType,
    val actorName: String,
    val at: String,
    val note: String = "",
)

enum class EntryTableKind {
    REVENUE,
    EXPENSE,
}

enum class OperationalEntryKind {
    GESTAO_FINANCEIRA,
    PROVISIONADOS_MES,
}

sealed interface EntryRowLabel {
    data class Resource(
        val value: StringResource,
    ) : EntryRowLabel

    data class Supplier(
        val value: String,
    ) : EntryRowLabel
}

data class Installment(
    val total: Int,
    val current: Int,
)

data class EntryRow(
    val id: String,
    val label: EntryRowLabel,
    val weeks: List<Long?> = emptyList(),
    val supplierId: String? = null,
    val installment: Installment? = null,
) {
    val totalCents: Long
        get() = weeks.filterNotNull().sum()
}

data class EntryTable(
    val id: String,
    val title: StringResource,
    val kind: EntryTableKind,
    val rows: List<EntryRow> = emptyList(),
) {
    val totalCents: Long
        get() = rows.sumOf { it.totalCents }
}

data class OperationalEntry(
    val id: String,
    val unidadeId: String = "",
    val kind: OperationalEntryKind = OperationalEntryKind.GESTAO_FINANCEIRA,
    val companyId: Int,
    val companyName: String,
    val period: MonthPeriod,
    val tables: List<EntryTable> = emptyList(),
    val status: OperationalEntryStatus = OperationalEntryStatus.DRAFT,
    val versao: Int = 1,
    val oficial: Boolean = false,
    val serverEditable: Boolean? = null,
    val submittedBy: String = "",
    val submittedAt: String? = null,
    val reviewedBy: String = "",
    val reviewedAt: String? = null,
    val rejectionReason: String = "",
    val updatedBy: String = "",
    val updatedAt: String = "",
    val corporatePayrollCents: Long = 0L,
    val adminOthersPercent: Double = DEFAULT_ADMIN_OTHERS_PERCENT,
    val taxesExempt: Boolean = false,
    val staffMovements: List<StaffMovement> = emptyList(),
    val history: List<OperationalEntryEvent> = emptyList(),
) {
    val editable: Boolean
        get() =
            // Janela de edição (3º dia útil) desabilitada por enquanto: o botão de editar
            // aparece sempre após a aprovação. Para reativar, use o `serverEditable` vindo
            // do backend (que já calcula a janela):
            // serverEditable ?: (status == DRAFT || status == REJECTED || status == APPROVED)
            status == OperationalEntryStatus.DRAFT ||
                status == OperationalEntryStatus.REJECTED ||
                status == OperationalEntryStatus.APPROVED

    val revenueTotalCents: Long
        get() = tables.filter { it.kind == EntryTableKind.REVENUE }.sumOf { it.totalCents }

    val expenseTotalCents: Long
        get() = tables.filter { it.kind == EntryTableKind.EXPENSE }.sumOf { it.totalCents } + corporateTotalCents

    val resultCents: Long
        get() = revenueTotalCents - expenseTotalCents

    val resultPercent: Double
        get() = if (revenueTotalCents == 0L) 0.0 else resultCents.toDouble() / revenueTotalCents * 100.0

    val corporateChargesCents: Long
        get() = corporatePayrollCents * CHARGES_PERCENT / 100

    val corporateAdminOthersCents: Long
        get() = (revenueTotalCents * adminOthersPercent / 100).roundToLong()

    val corporateTaxesCents: Long
        get() = if (taxesExempt) 0L else revenueTotalCents * TAXES_PER_MILLE / 1000

    val corporateTotalCents: Long
        get() = corporatePayrollCents + corporateChargesCents + corporateAdminOthersCents + corporateTaxesCents

    private companion object {
        const val CHARGES_PERCENT = 105L
        const val TAXES_PER_MILLE = 96L
    }
}

fun OperationalEntry.withWeek(
    rowId: String,
    weekIndex: Int,
    cents: Long?,
): OperationalEntry =
    copy(
        tables =
            tables.map { table ->
                table.copy(
                    rows =
                        table.rows.map { row ->
                            if (row.id != rowId) {
                                row
                            } else {
                                row.copy(
                                    weeks =
                                        row.weeks.toMutableList().apply {
                                            while (size <= weekIndex) add(null)
                                            this[weekIndex] = cents
                                        },
                                )
                            }
                        },
                )
            },
    )

fun OperationalEntry.withSupplierRow(
    categoryId: String,
    row: EntryRow,
): OperationalEntry =
    copy(
        tables =
            tables.map { table ->
                if (table.id == categoryId) table.copy(rows = table.rows + row) else table
            },
    )

fun OperationalEntry.updatedRow(
    rowId: String,
    transform: (EntryRow) -> EntryRow,
): OperationalEntry =
    copy(
        tables =
            tables.map { table ->
                table.copy(
                    rows = table.rows.map { row -> if (row.id == rowId) transform(row) else row },
                )
            },
    )

fun OperationalEntry.withoutRow(rowId: String): OperationalEntry =
    copy(
        tables =
            tables.map { table ->
                table.copy(rows = table.rows.filterNot { it.id == rowId })
            },
    )

fun OperationalEntry.cleared(weekCount: Int): OperationalEntry = copy(tables = OperationalEntryCatalog.emptyTables(weekCount))

fun OperationalEntry.saved(
    by: String,
    at: String,
): OperationalEntry = copy(updatedBy = by, updatedAt = at).withEvent(OperationalEntryEventType.SAVED, by, at)

fun OperationalEntry.submitted(
    by: String,
    at: String,
): OperationalEntry =
    copy(
        status = OperationalEntryStatus.SUBMITTED,
        submittedBy = by,
        submittedAt = at,
        updatedBy = by,
        updatedAt = at,
    ).withEvent(OperationalEntryEventType.SUBMITTED, by, at)

fun OperationalEntry.approved(
    by: String,
    at: String,
): OperationalEntry =
    copy(
        status = OperationalEntryStatus.APPROVED,
        reviewedBy = by,
        reviewedAt = at,
        rejectionReason = "",
        updatedBy = by,
        updatedAt = at,
    ).withEvent(OperationalEntryEventType.APPROVED, by, at)

fun OperationalEntry.rejected(
    by: String,
    at: String,
    reason: String,
): OperationalEntry =
    copy(
        status = OperationalEntryStatus.REJECTED,
        reviewedBy = by,
        reviewedAt = at,
        rejectionReason = reason,
        updatedBy = by,
        updatedAt = at,
    ).withEvent(OperationalEntryEventType.REJECTED, by, at, reason)

private fun OperationalEntry.withEvent(
    type: OperationalEntryEventType,
    actorName: String,
    at: String,
    note: String = "",
): OperationalEntry =
    copy(
        history = history + OperationalEntryEvent(type = type, actorName = actorName, at = at, note = note),
    )
